import * as yup from "yup";

import Alert from '@mui/material/Alert';
import { AlertMessage } from '../../lib/util/AlertMessage';
import { Auth } from "aws-amplify";
import Box from '@mui/material/Box';
import { CognitoUser } from "amazon-cognito-identity-js";
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import LoadingButton from "@mui/lab/LoadingButton";
import { PasswordField } from "./PasswordField";
import Typography from '@mui/material/Typography';
import { passwordSchema } from "./PasswordSchema";
import { useFormik } from "formik";
import { useState } from "react";

const forceNewPasswordFormSchema = yup.object({
    password: passwordSchema,
    confirmPassword: yup.string().oneOf([yup.ref("password"), null], "Your passwords must match").required()
});

type ForceNewPasswordFormData = yup.InferType<typeof forceNewPasswordFormSchema>;

type ForceNewPasswordFormProps = {
    user: CognitoUser;
};

export const ForceNewPasswordForm = ({ user }: ForceNewPasswordFormProps) => {
    const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
    const [isSettingPassword, setIsSettingPassword] = useState<boolean>(false);

    const changePassword = async (data: ForceNewPasswordFormData) => {
        try {
            setIsSettingPassword(true);
            setAlertMessage(undefined);
            await Auth.completeNewPassword(user, data.password);
            setAlertMessage({
                severity: "info",
                message: `Password changed.`
            });
        } catch (error) {
            console.log("Error changing password", error);
            setAlertMessage({
                severity: "error",
                message: (error as Error).message
            });
        } finally {
            setIsSettingPassword(false);
        }
    };

    const formik = useFormik<ForceNewPasswordFormData>({
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        validationSchema: forceNewPasswordFormSchema,
        onSubmit: changePassword
    });

    return (
        <Container component="main" maxWidth="xs">
            <Box
                mt={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
            >
                <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h5" fontWeight="500">
                                Password Change Required
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <PasswordField
                                id="password"
                                name="password"
                                label="Password"
                                autoComplete="new-password"
                                onChange={formik.handleChange}
                                value={formik.values.password}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PasswordField
                                id="confirmPassword"
                                name="confirmPassword"
                                label="Confirm Password"
                                autoComplete="new-password"
                                onChange={formik.handleChange}
                                value={formik.values.confirmPassword}
                                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                            />
                        </Grid>
                        {alertMessage &&
                            <Grid item xs={12}>
                                <Alert severity={alertMessage.severity}> {alertMessage.message}</Alert>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <LoadingButton
                                type="submit"
                                fullWidth
                                variant="contained"
                                loading={isSettingPassword}
                                startIcon={<></>}
                                loadingPosition="start"
                            >
                                Submit
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Container>
    );
};